import { useParams, useNavigate } from "react-router-dom";
import React, { useState, useContext, useEffect, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { AppContext } from "../../context/AppContext";
import Swal from "sweetalert2";
import axios from "axios";
import GetToken from "../../GetToken";
import { handleInvalidToken } from "../../AuthUtils";
import AnimationSpinner from "../../AnimationSpinner";

import Button from "@mui/material/Button";

import Select from "react-select";

import { FaKeyboard } from "react-icons/fa";
import { FaCheck } from "react-icons/fa";
import { MdPublish } from "react-icons/md";

//react icons
import { FaRegPlusSquare } from "react-icons/fa";
import { FaRegTrashAlt } from "react-icons/fa";

//import for react drag and drop
import { toast } from "react-toastify";
import { useDropzone } from "react-dropzone";

import CustomInput from "../../components/custom-components/CustomInput";
import FileUploader from "../../components/custom-components/FileUploader";
import TalkingBookStepper from "../../components/custom-components/BookFlow/TalkingBookStepper";
import { IoIosArrowRoundBack, IoIosArrowRoundForward } from "react-icons/io";

const BookForm = () => {
    const [selectedTitle, setSelectedTitle] = useState(null);
    const [titleResult, setTitleResult] = useState([]);
    const [languageOptions, setLanguageOptions] = useState([]);
    const { type } = useParams();
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const { base_url } = useContext(AppContext);
    const [photos, setPhotos] = useState([]);
    const [activeStepper, setActiveStepper] = useState(0);
    const talkingBookStepper = ['Basic details', 'Book Cover', 'Index', 'Customize Book', 'Avatar Customization'];
    const [uploadedPdf, setUploadedPdf] = useState([]);

    const toneOptions = [
        { label: "Serious", value: "serious" },
        { label: "Humorous", value: "humorous" },
        { label: "Motivational", value: "motivational" },
        { label: "Cautious", value: "cautious" },
        { label: "Optimistic", value: "optimistic" },
        { label: "Pessimistic", value: "pessimistic" },
        { label: "Friendly", value: "friendly" },
        { label: "Aggressive", value: "aggressive" },
        { label: "Confident", value: "confident" },
        { label: "Neutral", value: "neutral" },
        { label: "Melancholic", value: "melancholic" },
        { label: "Romantic", value: "romantic" },
        { label: "Ironic", value: "ironic" },
        { label: "Sarcastic", value: "sarcastic" },
        { label: "Empathetic", value: "empathetic" },
        { label: "Nostalgic", value: "nostalgic" },
        { label: "Inquisitive", value: "inquisitive" },
        { label: "Regretful", value: "regretful" },
        { label: "Authoritative", value: "authoritative" },
        { label: "Gloomy", value: "gloomy" },
        { label: "Dramatic", value: "dramatic" },
        { label: "Inspirational", value: "inspirational" },
        { label: "Whimsical", value: "whimsical" },
        { label: "Dark", value: "dark" },
        { label: "Suspenseful", value: "suspenseful" },
        { label: "Mysterious", value: "mysterious" },
        { label: "Hopeful", value: "hopeful" },
        { label: "Adventurous", value: "adventurous" },
        { label: "Eerie", value: "eerie" },
        { label: "Playful", value: "playful" },
    ];

    const writingStyles = [
        { label: "Narrative", value: "narrative" },
        { label: "Formal", value: "formal" },
        { label: "Informal/Casual", value: "informal/casual" },
        { label: "Descriptive", value: "descriptive" },
        { label: "Expository", value: "expository" },
        { label: "Professional", value: "professional" },
        { label: "Persuasive", value: "persuasive" },
        { label: "Poetic", value: "poetic" },
        { label: "Technical", value: "technical" },
        { label: "Journalistic", value: "journalistic" },
        { label: "Satirical", value: "satirical" },
        { label: "Academic", value: "academic" },
        { label: "Reflective", value: "reflective" },
        { label: "Imaginative/Fantastical", value: "imaginative/fantastical" },
        { label: "Gothic", value: "gothic" },
        { label: "Mystery/Thriller", value: "mystery/thriller" },
        { label: "Science Fiction", value: "science-fiction" },
        { label: "Dystopian", value: "dystopian" },
        { label: "Epic", value: "epic" },
        { label: "Fairy Tale", value: "fairy-tale" },
        { label: "Horror", value: "horror" },
        { label: "Mythological", value: "mythological" },
        { label: "Surreal", value: "surreal" },
        { label: "Realistic Fiction", value: "realistic-fiction" },
        { label: "Adventure", value: "adventure" },
        { label: "Crime/Detective", value: "crime/detective" },
    ];

    const chapterWords = [
        { label: "100 Characters", value: 100 },
        { label: "200 Characters", value: 200 },
        { label: "300 Characters", value: 300 },
        { label: "400 Characters", value: 400 },
        { label: "500 Characters", value: 500 },
        { label: "600 Characters", value: 600 },
        { label: "700 Characters", value: 700 },
    ];


    // talking book functions
    const formik = useFormik({
        initialValues: {
            keyword: "",
        },
        validationSchema: Yup.object({
            keyword: Yup.string()
                .required("Please enter a keyword")
                .max(200, "Keyword cannot exceed 200 characters"),
        }),
        onSubmit: async (values) => {
            const token = GetToken();
            if (!token) {
                handleInvalidToken(navigate);
                return;
            }

            setLoading(true);
            setSelectedTitle(null);
            const formData = new FormData();
            Object.entries(values).forEach(([key, value]) =>
                formData.append(key, value)
            );
            formData.append("token", token);
            formData.append("action", "generateBookTitle");
            formData.append("type", type);

            try {
                const response = await axios.post(`${base_url}api/user/book`, formData);
                if (response.data.status && response.data.bookTitles) {
                    setTitleResult(response.data.bookTitles);
                    setLoading(false);
                    const sortedLanguages = response.data.languages.sort((a, b) =>
                        a.language_name.localeCompare(b.language_name)
                    );
                    setLanguageOptions(sortedLanguages);
                } else {
                    setLoading(false);
                    if (response.data.reset) {
                        handleInvalidToken(navigate);
                        return;
                    }
                    toast.info(response.data.message ||
                        "Unable to generate book titles. Please try again.",)
                    // Swal.fire({
                    //     icon: "info",
                    //     title: "Alert",
                    //     text:
                    //         response.data.message ||
                    //         "Unable to generate book titles. Please try again.",
                    // });
                }
            } catch (error) {
                setLoading(false);
                toast.error(error.response?.data?.message ||
                    "Something went wrong. Please try again later.",)
                // Swal.fire({
                //     icon: "error",
                //     title: "Error",
                //     text:
                //         error.response?.data?.message ||
                //         "Something went wrong. Please try again later.",
                // });
            }
        },
    });

    const formik1 = useFormik({
        initialValues: {
            title: selectedTitle || "",
            writing_styles: writingStyles[0]?.value || "",
            language: "en-US",
            tone: toneOptions[0]?.value || "",
            chapters: 5,
            words_per_chapter: chapterWords[0]?.value || "",
        },
        validationSchema: Yup.object({
            title: Yup.string()
                .required("Please enter a book title.")
                .max(200, "Title cannot exceed 200 characters"),
            writing_styles: Yup.string().required("Please select a writing style."),
            language: Yup.string().required("Please select a language."),
            tone: Yup.string().required("Please select a tone."),
            chapters: Yup.number()
                .min(1, "Chapters must be at least 1.")
                .max(20, "Chapters cannot exceed 20.")
                .required("Please enter the number of chapters."),
            words_per_chapter: Yup.number().required(
                "Please enter characters per chapter."
            ),
        }),
        onSubmit: async (values) => {
            const token = GetToken();
            if (!token) {
                handleInvalidToken(navigate);
                return;
            }

            setLoading(true);

            const formData = new FormData();
            Object.entries(values).forEach(([key, value]) =>
                formData.append(key, value)
            );
            formData.append("token", token);

            formData.append("action", 'generateTalkingBookChapter');
            formData.append("type", type);

            try {
                const response = await axios.post(`${base_url}api/user/book`, formData);
                setLoading(false);

                if (response.data.status && response.data.book_token) {
                    navigate(`/book/edit/${response.data.book_token}`);
                    // Swal.fire({
                    //     icon: "success",
                    //     title: "Book Created!",
                    //     text: "Your book has been successfully generated.",
                    // });
                } else {
                    if (response.data.reset) {
                        handleInvalidToken(navigate);
                        return;
                    }
                    toast.info(response.data.message ||
                        "Unable to create the book. Please try again.");
                    // Swal.fire({
                    //     icon: "info",
                    //     title: "Attention",
                    //     text:
                    //         response.data.message ||
                    //         "Unable to create the book. Please try again.",
                    // });
                }
            } catch (error) {
                setLoading(false);
                toast.error(error.response?.data?.message ||
                    "Something went wrong. Please try again later.");
                // Swal.fire({
                //     icon: "error",
                //     title: "Error",
                //     text:
                //         error.response?.data?.message ||
                //         "Something went wrong. Please try again later.",
                // });
            }
        },
    });
    const handleSelectTitle = (title) => {
        setSelectedTitle(title);
    };
    useEffect(() => {
        formik1.setFieldValue("title", selectedTitle);
    }, [selectedTitle]);


    // photobook functions
    const photoBook_submit = useFormik({
        initialValues: {
            photo_book_title: "",
        },
        validationSchema: Yup.object({
            photo_book_title: Yup.string()
                .required("Please enter a book title.")
                .max(200, "Title cannot exceed 200 characters"),
        }),
        onSubmit: async (values) => {
            const token = GetToken();
            if (!token) {
                handleInvalidToken(navigate);
                return;
            }

            if (!photos || photos.length === 0) {
                toast.info("Please upload at least one image.");
                // Swal.fire({
                //     icon: "info",
                //     title: "Attention",
                //     text: "Please upload at least one image.",
                // });
            }
            setLoading(true);

            const formData = new FormData();
            formData.append("title", values.photo_book_title);
            formData.append("token", token);
            formData.append("action", 'generatePhotoBook');
            formData.append("type", type);

            photos.forEach((photo, index) => {
                formData.append(`images[]`, photo.file);
            });

            try {
                const response = await axios.post(`${base_url}api/user/book`, formData);
                setLoading(false);

                if (response.data.status && response.data.book_token) {
                    toast.success("Your PhotoBook has been successfully generated.");
                    // Swal.fire({
                    //     icon: "success",
                    //     title: "PhotoBook Created!",
                    //     text: "Your PhotoBook has been successfully generated.",
                    // });
                    navigate(`/book/manage/photobook`);
                } else {
                    if (response.data.reset) {
                        handleInvalidToken(navigate);
                        return;
                    }
                    toast.info(response.data.message ||
                        "Unable to create the book. Please try again.");
                    // Swal.fire({
                    //     icon: "info",
                    //     title: "Attention",
                    //     text:
                    //         response.data.message ||
                    //         "Unable to create the book. Please try again.",
                    // });
                }
            } catch (error) {
                setLoading(false);
                toast.error(error.response?.data?.message ||
                    "Something went wrong. Please try again later.");
                // Swal.fire({
                //     icon: "error",
                //     title: "Error",
                //     text:
                //         error.response?.data?.message ||
                //         "Something went wrong. Please try again later.",
                // });
            }
        },
    });

    const handleRemovePhoto = (id) => {
        setPhotos(photos.filter((photo) => photo.id !== id))
    }

    // photoBook functions end

    // flipbook functions
    const flipBook_submit = useFormik({
        initialValues: {
            flip_book_title: "",
        },
        validationSchema: Yup.object({
            flip_book_title: Yup.string()
                .required("Please enter a book title.")
                .max(200, "Title cannot exceed 200 characters"),
        }),
        onSubmit: async (values) => {
            const token = GetToken();
            if (!token) {
                handleInvalidToken(navigate);
                return;
            }

            if (!uploadedPdf || uploadedPdf.length === 0) {
                toast.info("Please upload pdf.");
                // Swal.fire({
                //     icon: "info",
                //     title: "Attention",
                //     text: "Please upload pdf.",
                // });
            }
            setLoading(true);

            const formData = new FormData();

            formData.append("action", 'generateFlipBook');
            formData.append("token", token);
            formData.append("title", values.flip_book_title);
            formData.append("type", type);
            formData.append("pdf", uploadedPdf[0].file);
            // console.log(formData);

            try {
                const response = await axios.post(`${base_url}api/user/book`, formData);
                setLoading(false);

                if (response.data.status && response.data.book_token) {
                    navigate(`/book/manage/flipbook`);
                } else {
                    if (response.data.reset) {
                        handleInvalidToken(navigate);
                        return;
                    }
                    toast.info(response.data.message ||
                        "Unable to create the book. Please try again.");
                    // Swal.fire({
                    //     icon: "info",
                    //     title: "Attention",
                    //     text:
                    //         response.data.message ||
                    //         "Unable to create the book. Please try again.",
                    // });
                }
            } catch (error) {
                setLoading(false);
                toast.error(error.response?.data?.message ||
                    "Something went wrong. Please try again later.");
                // Swal.fire({
                //     icon: "error",
                //     title: "Error",
                //     text:
                //         error.response?.data?.message ||
                //         "Something went wrong. Please try again later.",
                // });
            }
        },
    });

    const handleFlipBookFileChange = (files) => {
        if (files.length > 0) {
            const pdf = files[0];
            console.log("Selected file:", pdf);
            flipBook_submit.setFieldValue("file", pdf);
        } else {
            flipBook_submit.setFieldValue("file", null);
        }
    };

    // flipbook functions end

    const handleNext = () => {
        setStep((prev) => prev + 1);
    };
    const handleBack = () => setStep((prev) => prev - 1);

    return (
        <>
            <AnimationSpinner show={loading} />
            <div className="container p-0">
                <div className="right-container">
                    {type === "talking_book" && (
                        <>
                            {step === 1 && (
                                <>
                                    <div className="row align-items-center">
                                        <div className="col-lg-6 page-title-container">
                                            <h4 className="fw-semibold mb-1">Create Talking Book</h4>
                                            <p className="mb-1">Generate title for your book</p>
                                        </div>
                                        <div className="col-lg-6 justify-content-end">
                                            <div className="d-flex justify-content-end gap-4">
                                                <Button
                                                    color="secondary"
                                                    variant="contained"
                                                    onClick={() => {
                                                        navigate("/book/manage/talking_book")
                                                    }}
                                                    title="Go to Book List"
                                                >
                                                    Go to Book List
                                                    <IoIosArrowRoundForward
                                                        size={30}
                                                        fontWeight={600}
                                                        color="white"
                                                    />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row my-4">
                                        <TalkingBookStepper steps={talkingBookStepper} activeStep={activeStepper} />
                                    </div>
                                    <div className="card mt-4">
                                        <div className="card-body">
                                            <form onSubmit={formik.handleSubmit}>
                                                <h5>Enter a keyword to generate title for your book</h5>

                                                <div className="mb-1 d-lg-flex mt-3 ">
                                                    <input
                                                        type="text"
                                                        name="keyword"
                                                        id="keyword"
                                                        className="form-control mb-2 mb-lg-0"
                                                        placeholder="Enter keyword - Animal, Cartoon, Story "
                                                        value={formik.values.keyword}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                        style={{
                                                            width: "100%",
                                                            borderTopRightRadius: "0px",
                                                            borderBottomRightRadius: "0px",
                                                        }}
                                                    />
                                                    <Button
                                                        type="submit"
                                                        variant="contained"
                                                        color="primary"
                                                        style={{
                                                            width: "250px",
                                                            borderTopLeftRadius: "0px",
                                                            borderBottomLeftRadius: "0px",
                                                        }}
                                                    >
                                                        Generate Book Title
                                                    </Button>
                                                </div>
                                                {formik.touched.keyword && formik.errors.keyword ? (
                                                    <div
                                                        className="text-danger"
                                                        style={{ fontSize: "0.875rem" }}
                                                    >
                                                        {formik.errors.keyword}
                                                    </div>
                                                ) : null}
                                            </form>

                                            {titleResult.length > 0 ? (
                                                <>
                                                    <div
                                                        className="d-flex flex-column my-3 px-3"
                                                        style={{
                                                            minHeight: "250px",
                                                            maxHeight: "370px",
                                                            overflowY: "auto",
                                                            scrollbarWidth: "none",
                                                        }}
                                                    >
                                                        {titleResult.map((title, index) => (
                                                            <div
                                                                key={index}
                                                                className={`d-flex align-items-center my-1 bg-transparent title-item  p-2  pointer ddd ${selectedTitle} ${title} ${selectedTitle === title
                                                                    ? "bg-selected"
                                                                    : "bg-light"
                                                                    }`}
                                                                onClick={() => handleSelectTitle(title)}
                                                            >
                                                                <div
                                                                    className={`selected_title  ${selectedTitle == title
                                                                        ? "d-block"
                                                                        : "d-none"
                                                                        }`}
                                                                >
                                                                    <FaCheck />
                                                                </div>
                                                                <span>{title}</span>
                                                            </div>
                                                        )
                                                        )}
                                                    </div>
                                                    {selectedTitle && (
                                                        <div className="d-flex justify-content-end align-items-center mt-4">
                                                            <Button
                                                                color="success"
                                                                variant="contained"
                                                                onClick={handleNext}
                                                            >
                                                                Next
                                                                <IoIosArrowRoundForward
                                                                    size={30}
                                                                    fontWeight={600}
                                                                    color="white"
                                                                />
                                                            </Button>
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                <div
                                                    className="d-flex flex-column justify-content-center align-items-center my-3"
                                                    style={{ minHeight: "250px" }}
                                                >
                                                    <FaKeyboard size={100} />
                                                    <p className="mt-2">
                                                        Provide a keyword to create a custom book title
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}
                            {step === 2 && (
                                <>
                                    <div className="row align-items-center">
                                        <div className="col-lg-6 page-title-container">
                                            <h4 className="fw-semibold mb-1">Create Talking book</h4>
                                            <p className="mb-1">
                                                Select additional details for talking book
                                            </p>
                                        </div>
                                        <div className="col-lg-6 justify-content-end">
                                            <div className="d-flex justify-content-end gap-4">
                                                <Button
                                                    color="secondary"
                                                    variant="contained"
                                                    onClick={handleBack}
                                                    title="Go Back"
                                                >
                                                    <IoIosArrowRoundBack
                                                        size={30}
                                                        fontWeight={600}
                                                        color="white"
                                                    />
                                                    Back{" "}
                                                </Button>

                                                <Button
                                                    onClick={formik1.handleSubmit}
                                                    color="secondary"
                                                    variant="contained"
                                                    title="Next Step"
                                                >
                                                    Go to next Step
                                                    <IoIosArrowRoundForward
                                                        size={30}
                                                        fontWeight={600}
                                                        color="white"
                                                    />
                                                </Button>

                                            </div>
                                        </div>
                                    </div>
                                    <div className="row my-4">
                                        <TalkingBookStepper steps={talkingBookStepper} activeStep={activeStepper} />
                                    </div>
                                    <div className="card mt-4 overflow-visible">
                                        <div className="card-body overflow-visible">
                                            <form onSubmit={formik1.handleSubmit}>
                                                <div className="row">
                                                    <div className="col-lg-12">
                                                        <CustomInput
                                                            label="Title"
                                                            name="title"
                                                            formik={formik1}
                                                            type="text"
                                                        />
                                                    </div>
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label">Writing Styles</label>
                                                        <Select
                                                            menuPlacement="auto"
                                                            options={writingStyles}
                                                            name="writing_styles"
                                                            value={writingStyles.find(
                                                                (option) =>
                                                                    option.value === formik1.values.writing_styles
                                                            )}
                                                            onChange={(selectedOption) => {
                                                                formik1.setFieldValue(
                                                                    "writing_styles",
                                                                    selectedOption.value
                                                                );
                                                            }}
                                                            onBlur={() =>
                                                                formik1.setFieldTouched("writing_styles", true)
                                                            }
                                                            placeholder="Select Style"
                                                        />
                                                        {formik1.touched.writing_styles &&
                                                            formik1.errors.writing_styles ? (
                                                            <div
                                                                className="text-danger"
                                                                style={{ fontSize: "0.875rem" }}
                                                            >
                                                                {formik1.errors.writing_styles}
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label">Language</label>
                                                        <select
                                                            className="form-select form-control"
                                                            name="language"
                                                            value={formik1.values.language || "en-US"}
                                                            onChange={formik1.handleChange}
                                                            onBlur={formik1.handleBlur}
                                                            style={{ fontSize: "0.875rem" }}
                                                            placeholder="Select Language"
                                                        >
                                                            {languageOptions.map((lang, index) => (
                                                                <option key={index} value={lang.language_code}>
                                                                    {lang.language_name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        {formik1.touched.language &&
                                                            formik1.errors.language && (
                                                                <div
                                                                    className="text-danger"
                                                                    style={{ fontSize: "0.875rem" }}
                                                                >
                                                                    {formik1.errors.language.label}
                                                                </div>
                                                            )}
                                                    </div>
                                                    <div className="col-lg-4 mb-3">
                                                        <label className="form-label">Tone</label>
                                                        <Select
                                                            menuPlacement="auto"
                                                            name="tone"
                                                            options={toneOptions}
                                                            value={toneOptions.find(
                                                                (option) => option.value === formik1.values.tone
                                                            )}
                                                            onChange={(selectedOption) => {
                                                                formik1.setFieldValue(
                                                                    "tone",
                                                                    selectedOption.value
                                                                );
                                                            }}
                                                            onBlur={() =>
                                                                formik1.setFieldTouched("tone", true)
                                                            }
                                                            placeholder="Select Tone"
                                                        />
                                                        {formik1.touched.tone && formik1.errors.tone && (
                                                            <div
                                                                className="text-danger"
                                                                style={{ fontSize: "0.875rem" }}
                                                            >
                                                                {formik1.errors.tone.label}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="col-lg-4 nb-3">
                                                        <CustomInput
                                                            label="Chapters"
                                                            name="chapters"
                                                            formik={formik1}
                                                            type="number"
                                                            min={1}
                                                            max={20}
                                                        />
                                                    </div>
                                                    <div className="col-lg-4 mb-3">
                                                        <label className="form-label">
                                                        Characters per chapter
                                                        </label>
                                                        <Select
                                                            name="words_per_chapter"
                                                            options={chapterWords}
                                                            value={chapterWords.find(
                                                                (option) =>
                                                                    option.value ===
                                                                    formik1.values.words_per_chapter
                                                            )}
                                                            onChange={(selectedOption) => {
                                                                formik1.setFieldValue(
                                                                    "words_per_chapter",
                                                                    selectedOption.value
                                                                );
                                                            }}
                                                            onBlur={() =>
                                                                formik1.setFieldTouched(
                                                                    "words_per_chapter",
                                                                    true
                                                                )
                                                            }
                                                            placeholder="Select Words per Chapter"
                                                            menuPlacement="auto"
                                                        />
                                                        {formik1.touched.words_per_chapter &&
                                                            formik1.errors.words_per_chapter && (
                                                                <div
                                                                    className="text-danger"
                                                                    style={{ fontSize: "0.875rem" }}
                                                                >
                                                                    {formik1.errors.words_per_chapter.label}
                                                                </div>
                                                            )}
                                                    </div>
                                                </div>
                                            </form>
                                        </div>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                    {
                        type === "photobook" && (
                            <div className="photo-book">
                                <div className="row align-items-center">
                                    <div className="col-lg-6 page-title-container">
                                        <h4 className="fw-semibold mb-1">Create PhotoBook</h4>
                                        <p className="mb-1">Upload photos to create a PhotoBook</p>
                                    </div>
                                    <div className="col-lg-6 justify-content-end">
                                        <div className="d-flex justify-content-end gap-4">
                                            <Button
                                                color="secondary"
                                                variant="contained"
                                                onClick={() => navigate("/book/manage/photobook")}
                                            >
                                                Go to Book List
                                                <IoIosArrowRoundForward
                                                    size={30}
                                                    fontWeight={600}
                                                    color="white"
                                                />
                                            </Button>
                                            {photos.length > 0 &&
                                                <Button
                                                    onClick={photoBook_submit.handleSubmit}
                                                    color="success"
                                                    variant="contained"
                                                >

                                                    Publish Book
                                                    <MdPublish />
                                                </Button>}
                                        </div>
                                    </div>
                                </div>
                                <div className="card mt-4">

                                    <div className="card-body">
                                        <div className="row">
                                            <CustomInput
                                                label="Enter PhotoBook Title"
                                                name="photo_book_title"
                                                placeholder="Enter PhotoBook Title"
                                                formik={photoBook_submit}
                                                type="text"
                                            />
                                            <div className={photos.length > 0 ? "col-md-6" : "col-md-12"}>
                                                <div className="my-2">
                                                    <p className="m-0 ps-3 text-primary">*Note: Please use a 9:16 ratio in portrait size.</p>

                                                </div>
                                                <FileUploader
                                                    multiple={true}
                                                    // onUploadComplete={handlePhotoBookUploadComplete}
                                                    allowedFormats={["image/png", "image/jpeg"]}
                                                    maxFileSize={3 * 1024 * 1024}
                                                    maxFiles={20}
                                                    dropzoneStyles={{ backgroundColor: "#f8f9fa" }}
                                                    progressBarColor="#28a745"
                                                    uploadText={"Upload or drag your Images to create your Photo Book Maker. "}
                                                    files={photos}
                                                    setFiles={setPhotos}
                                                />
                                            </div>
                                            {photos.length > 0 &&
                                                <div className="col-md-6" style={{ maxHeight: "490px", overflow: "scroll", scrollbarWidth: "none" }}>
                                                    <div className="row mb-3 text-center">
                                                        <h4 >Photos Preview</h4>
                                                    </div>
                                                    <div className="row">
                                                        {photos.map((photo, index) => {
                                                            const preview = URL.createObjectURL(photo.file)
                                                            return (
                                                                <div className="col-md-4" key={index} >
                                                                    <div key={index} className="photo-container" style={{ width: "100%", height: "180px", overflow: "hidden", }}>
                                                                        <img src={preview} alt={`Photo ${index + 1}`} className="photo" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                                                                        <button className="remove-button bg-red text-white" onClick={() => handleRemovePhoto(photo.id)}>
                                                                            <FaRegTrashAlt />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            )
                                                        })}
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    }

                    {type === "flipbook" && (
                        <div className="photo-book">
                            <div className="row align-items-center">
                                <div className="col-lg-6 page-title-container">
                                    <h4 className="fw-semibold mb-1">Create FlipBook</h4>
                                    <p className="mb-1">Upload or drag your PDF file to transform it into a flipbook.</p>
                                </div>
                                <div className="col-lg-6 justify-content-end">
                                    <div className="d-flex justify-content-end gap-4">
                                        <Button
                                            color="secondary"
                                            variant="contained"
                                            onClick={() => {
                                                navigate("/book/manage/flipbook")
                                            }}
                                            title="Go to Book List"
                                        >
                                            Go to Book List
                                            <IoIosArrowRoundForward
                                                size={30}
                                                fontWeight={600}
                                                color="white"
                                            />
                                        </Button>
                                        {uploadedPdf.length > 0 &&
                                            <Button
                                                onClick={flipBook_submit.handleSubmit}
                                                color="success"
                                                variant="contained"
                                            >
                                                <MdPublish className="me-2" size={18} />
                                                Publish FlipBook
                                            </Button>}
                                    </div>
                                </div>
                            </div>
                            <div className="card mt-4">

                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-md-12">
                                            <CustomInput
                                                label="Enter FlipBook Title"
                                                name="flip_book_title"
                                                placeholder="Enter FlipBook Title"
                                                formik={flipBook_submit}
                                                type="text"
                                            />
                                        </div>
                                        <div className="col-md-12">
                                            <FileUploader
                                                multiple={true}
                                                allowedFormats={["application/pdf"]}
                                                maxFileSize={10 * 1024 * 1024}
                                                maxFiles={10}
                                                dropzoneStyles={{ backgroundColor: "#f8f9fa" }}
                                                progressBarColor="#28a745"
                                                uploadText={"Upload or drag your Pdf to create your Flip Book . "}
                                                files={uploadedPdf}
                                                setFiles={setUploadedPdf}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}


                </div>
            </div>
        </>
    );
};

export default BookForm;
